import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';
import CustomTabs from 'components/CustomTabs';
import CardWithTitle from 'components/CardWithTitle';
import Loader from 'components/common/Loader';
import {
  getUserEventsClicks,
  getUserEventBreakdown,
  subscribeToUserEvents,
  subscribeToUserEventBreakdown,
  getUserOffersViews,
  getUserLoginFailures,
  formatDateTime,
  formatTypeDisplay,
} from 'services/firebaseAnalytics';

const SUB_TABS = {
  ALL: 'All',
  USER_ACTIVITY: 'User Activity & Clicks',
  VIEWED_GRADES: 'Viewed Grades & Demand',
  LOGIN_FAILURES: 'Login & OTP Failures',
};

const VendorFirebaseData = ({
  vendorId,
  vendor = null,
  embedded = false,
}) => {
  const [activeSubTab, setActiveSubTab] = useState(SUB_TABS.ALL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userDocId, setUserDocId] = useState(null);
  const [events, setEvents] = useState([]);
  const [gradeData, setGradeData] = useState([]);
  const [loginFailures, setLoginFailures] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedEventModal, setSelectedEventModal] = useState(null);

  // Fetch all Firebase data for this vendor
  const fetchData = useCallback(async () => {
    if (!vendorId) return;

    try {
      setLoading(true);
      setError(null);

      // 1. Fetch user_events_clicks for this user
      const { primaryUserDoc } = await getUserEventsClicks(vendorId);

      if (primaryUserDoc?.id) {
        setUserDocId(primaryUserDoc.id);
        const userEvents = await getUserEventBreakdown(primaryUserDoc.id);
        setEvents(userEvents);
      } else {
        setUserDocId(null);
        setEvents([]);
      }

      // 2. Fetch viewed grades from offers_views
      const viewedGrades = await getUserOffersViews(vendorId);
      setGradeData(viewedGrades);

      // 3. Fetch login failures matching vendor phone
      const phoneToMatch =
        vendor?.phone || primaryUserDoc?.mobileNumber || '';
      if (phoneToMatch) {
        const failures = await getUserLoginFailures(phoneToMatch);
        setLoginFailures(failures);
      } else {
        setLoginFailures([]);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching Firebase data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [vendorId, vendor]);

  // Initial fetch and subscription
  useEffect(() => {
    if (!vendorId) return () => {};

    fetchData();

    // Listen to user clicks document in real-time
    const unsubscribeClicks = subscribeToUserEvents(
      vendorId,
      (data) => {
        if (data?.primaryUserDoc?.id) {
          setUserDocId(data.primaryUserDoc.id);
        }
      },
      (err) => console.error('Subscription error for clicks:', err),
    );

    return () => {
      if (unsubscribeClicks) unsubscribeClicks();
    };
  }, [vendorId, fetchData]);

  // Real-time listener for events subcollection
  useEffect(() => {
    if (!userDocId) return () => {};

    const unsubscribeEvents = subscribeToUserEventBreakdown(
      userDocId,
      (updatedEvents) => {
        setEvents(updatedEvents);
      },
      (err) => console.error('Subscription error for events:', err),
    );

    return () => {
      if (unsubscribeEvents) unsubscribeEvents();
    };
  }, [userDocId]);

  // Reset pagination on sub-tab switch
  const handleTabChange = (tab) => {
    setActiveSubTab(tab);
    setCurrentPage(1);
  };

  // Combined data for "All" sub-tab (all activities together)
  const allData = useMemo(() => {
    const combined = [];

    // 1. User Activity & Clicks
    events.forEach((ev) => {
      combined.push({
        id: `activity-${ev.id || ev.buttonName}`,
        category: 'User Activity',
        badgeColor: 'primary',
        action: ev.buttonName,
        subAction: ev.extraData?.dialog_title
          ? `Dialog: ${ev.extraData.dialog_title}`
          : null,
        screen: ev.screenName || 'N/A',
        count: ev.count || 1,
        details: ev.userFlow || '-',
        extraData: ev.extraData || null,
        rawEvent: ev,
        timestamp: ev.lastClickedAt || null,
      });
    });

    // 2. Viewed Grades & Demand
    gradeData.forEach((grade) => {
      const hasMeta =
        grade.location &&
        grade.location !== 'N/A' &&
        grade.location !== '-';
      const metaText = hasMeta
        ? `${grade.location}${
            grade.rate && grade.rate !== 'N/A' && grade.rate !== '-'
              ? ` • ₹${grade.rate}`
              : ''
          }`
        : null;

      combined.push({
        id: `grade-${grade.gradeName}-${grade.type}`,
        category: 'Viewed Grade',
        badgeColor: 'success',
        action: grade.gradeName,
        subAction: metaText,
        screen: formatTypeDisplay(grade.type),
        count: grade.count || 1,
        details: grade.type ? `Type: ${formatTypeDisplay(grade.type)}` : '-',
        extraData: null,
        rawEvent: null,
        timestamp: grade.lastClicked || null,
      });
    });

    // 3. Login & OTP Failures
    loginFailures.forEach((fail) => {
      combined.push({
        id: `failure-${fail.id || fail.mobileNumber}`,
        category: 'Login Failure',
        badgeColor: 'danger',
        action: fail.errorReason,
        subAction: `Stage: ${fail.failureStage}`,
        screen: fail.screenName || 'N/A',
        count: 1,
        details: fail.mobileNumber ? `Mobile: ${fail.mobileNumber}` : '-',
        extraData: null,
        rawEvent: null,
        timestamp: fail.timestamp || null,
      });
    });

    // Sort descending by timestamp (latest events first)
    combined.sort((a, b) => {
      if (!a.timestamp && !b.timestamp) return 0;
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    return combined;
  }, [events, gradeData, loginFailures]);

  // Active dataset based on selected sub-tab
  const activeDataset = useMemo(() => {
    if (activeSubTab === SUB_TABS.ALL) return allData;
    if (activeSubTab === SUB_TABS.USER_ACTIVITY) return events;
    if (activeSubTab === SUB_TABS.VIEWED_GRADES) return gradeData;
    if (activeSubTab === SUB_TABS.LOGIN_FAILURES) return loginFailures;
    return allData;
  }, [activeSubTab, allData, events, gradeData, loginFailures]);

  // Pagination calculations (FSBTable pattern)
  const totalItems = activeDataset.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = activeDataset.slice(startIndex, endIndex);

  let content = null;

  if (loading) {
    content = <Loader />;
  } else if (error) {
    content = (
      <>
        <div className="text-danger f-12">
          Error loading Firebase data: {error}
        </div>
        <div className="text-muted f-11 mt-2">Check console for details</div>
      </>
    );
  } else {
    content = (
      <div>
        {/* Sub-Tabs inside History styled like Bids (Offer / Demand) */}
        <div className="d-flex align-items-center mb-3">
          <CustomTabs
            className="full-border-tab"
            tabs={[
              SUB_TABS.ALL,
              SUB_TABS.USER_ACTIVITY,
              SUB_TABS.VIEWED_GRADES,
              SUB_TABS.LOGIN_FAILURES,
            ]}
            tabsCount={[
              allData.length,
              events.length,
              gradeData.length,
              loginFailures.length,
            ]}
            selectedTab={activeSubTab}
            onTabChange={handleTabChange}
          />
        </div>

        {totalItems === 0 ? (
          <div className="mb-2 text-active f-12 py-3 text-center">
            No records found for this section
          </div>
        ) : (
          <>
            {/* SUB-TAB 0: ALL ACTIVITIES COMBINED */}
            {activeSubTab === SUB_TABS.ALL && (
              <div className="table-responsive">
                <table className="table table-hover table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525 text-center"
                        style={{ width: '60px' }}
                      >
                        S.No.
                      </th>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525"
                        style={{ width: '130px' }}
                      >
                        Type
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Activity / Item
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Screen
                      </th>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525 text-center"
                        style={{ width: '80px' }}
                      >
                        Count
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Details / Flow
                      </th>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525"
                        style={{ width: '160px' }}
                      >
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, index) => {
                      const sNo = startIndex + index + 1;
                      const rowKey = `${item.id}-${index}`;

                      let badgeClass = 'bg-primary text-white';
                      if (item.badgeColor === 'danger') {
                        badgeClass = 'bg-danger text-white';
                      } else if (item.badgeColor === 'success') {
                        badgeClass = 'bg-success text-white';
                      }

                      return (
                        <tr key={rowKey}>
                          <td className="text-center f-14 text-dark-525">
                            {sNo}
                          </td>
                          <td className="f-13">
                            <span
                              className={`badge ${badgeClass}`}
                              style={{
                                fontSize: '11px',
                                fontWeight: 500,
                                padding: '4px 8px',
                                borderRadius: '4px',
                              }}
                            >
                              {item.category}
                            </span>
                          </td>
                          <td className="f-14 fw-medium text-dark-525">
                            <div
                              className={
                                item.badgeColor === 'danger'
                                  ? 'text-danger'
                                  : ''
                              }
                            >
                              {item.action}
                            </div>
                            {item.subAction && (
                              <div className="f-11 text-muted mt-1">
                                {item.subAction}
                              </div>
                            )}
                          </td>
                          <td className="f-14 text-dark-525">{item.screen}</td>
                          <td className="text-center f-14 text-dark-525">
                            <span className="text-primary fw-semibold">
                              {item.count}
                            </span>
                          </td>
                          <td className="f-13 text-dark-525">
                            <div className="d-flex align-items-center justify-content-between">
                              <span>{item.details}</span>
                              {item.rawEvent && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 text-primary f-12 ms-2"
                                  onClick={() =>
                                    setSelectedEventModal(item.rawEvent)
                                  }
                                >
                                  Details
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="f-13 text-dark-525">
                            {formatDateTime(item.timestamp)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* SUB-TAB 1: USER ACTIVITY & CLICKS */}
            {activeSubTab === SUB_TABS.USER_ACTIVITY && (
              <div className="table-responsive">
                <table className="table table-hover table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525 text-center"
                        style={{ width: '60px' }}
                      >
                        S.No.
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Button / Action
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Screen
                      </th>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525 text-center"
                        style={{ width: '90px' }}
                      >
                        Clicks
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        User Navigation Flow
                      </th>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525 text-center"
                        style={{ width: '90px' }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((ev, index) => {
                      const sNo = startIndex + index + 1;
                      const rowKey = ev.id || `${ev.buttonName}-${ev.screenName}-${index}`;

                      return (
                        <tr key={rowKey}>
                          <td className="text-center f-14 text-dark-525">
                            {sNo}
                          </td>
                          <td className="f-14 fw-medium text-dark-525">
                            <div>{ev.buttonName}</div>
                            {ev.extraData?.dialog_title && (
                              <div className="f-11 text-muted">
                                Dialog: {ev.extraData.dialog_title}
                              </div>
                            )}
                          </td>
                          <td className="f-14 text-dark-525">{ev.screenName}</td>
                          <td className="text-center f-14 text-dark-525">
                            <span className="text-primary fw-semibold">
                              {ev.count}
                            </span>
                          </td>
                          <td className="f-13 text-dark-525">
                            {ev.userFlow || '-'}
                          </td>
                          <td className="text-center">
                            <Button
                              color="link"
                              size="sm"
                              className="p-0 text-primary f-13"
                              onClick={() => setSelectedEventModal(ev)}
                            >
                              Details
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* SUB-TAB 2: VIEWED GRADES & DEMAND */}
            {activeSubTab === SUB_TABS.VIEWED_GRADES && (
              <div className="table-responsive">
                <table className="table table-hover table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525 text-center"
                        style={{ width: '60px' }}
                      >
                        S.No.
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Grade Details
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Type
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((grade, index) => {
                      const sNo = startIndex + index + 1;
                      const hasMeta =
                        grade.location &&
                        grade.location !== 'N/A' &&
                        grade.location !== '-';
                      const rowKey = `${grade.gradeName}-${grade.type}-${index}`;

                      return (
                        <tr key={rowKey}>
                          <td className="text-center f-14 text-dark-525">
                            {sNo}
                          </td>
                          <td className="f-14 fw-medium text-dark-525">
                            <div>
                              {grade.gradeName}
                              <span className="text-primary fw-semibold">
                                {' '}
                                ({grade.count})
                              </span>
                            </div>
                            {hasMeta && (
                              <div className="f-11 text-muted mt-1">
                                {grade.location}
                                {grade.rate &&
                                  grade.rate !== 'N/A' &&
                                  grade.rate !== '-' &&
                                  ` • ₹${grade.rate}`}
                              </div>
                            )}
                          </td>
                          <td className="f-14 text-dark-525">
                            {formatTypeDisplay(grade.type)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* SUB-TAB 3: LOGIN & OTP FAILURES */}
            {activeSubTab === SUB_TABS.LOGIN_FAILURES && (
              <div className="table-responsive">
                <table className="table table-hover table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        className="f-12 fw-semibold text-uppercase text-dark-525 text-center"
                        style={{ width: '60px' }}
                      >
                        S.No.
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Failure Stage
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Error Reason
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Screen
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Mobile Number
                      </th>
                      <th className="f-12 fw-semibold text-uppercase text-dark-525">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((fail, index) => {
                      const sNo = startIndex + index + 1;
                      const rowKey =
                        fail.id || `${fail.mobileNumber}-${fail.failureStage}-${index}`;

                      return (
                        <tr key={rowKey}>
                          <td className="text-center f-14 text-dark-525">
                            {sNo}
                          </td>
                          <td className="f-14 text-dark-525">
                            {fail.failureStage}
                          </td>
                          <td className="f-14 text-danger fw-medium">
                            {fail.errorReason}
                          </td>
                          <td className="f-14 text-dark-525">
                            {fail.screenName}
                          </td>
                          <td className="f-14 text-dark-525">
                            {fail.mobileNumber}
                          </td>
                          <td className="f-13 text-dark-525">
                            {formatDateTime(fail.timestamp)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* FSBTable Pagination from Codebase */}
            {totalPages > 0 && (
              <div className="d-flex mt-3 align-items-center justify-content-end table-pagination-container">
                <span className="text-active text-nowrap f-12">
                  {currentPage} of {totalPages}
                </span>
                <select
                  className="page-select form-select table-select f-12 fw-normal mx-3 d-inline-block"
                  value={pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setPageSize(newSize);
                    setCurrentPage(1);
                  }}
                >
                  {[10, 20, 30, 40, 50].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
                <div className="pagination">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage <= 1}
                  >
                    {'<<'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage <= 1}
                  >
                    {'<'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage >= totalPages}
                  >
                    {'>'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                  >
                    {'>>'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Drilldown Details Modal */}
        <Modal
          isOpen={Boolean(selectedEventModal)}
          toggle={() => setSelectedEventModal(null)}
          centered
        >
          <ModalHeader toggle={() => setSelectedEventModal(null)}>
            User Action Details
          </ModalHeader>
          <ModalBody>
            {selectedEventModal && (
              <div>
                <div className="mb-2">
                  <span className="text-muted f-12 d-block">Action / Button</span>
                  <span className="fw-semibold f-14 text-dark">
                    {selectedEventModal.buttonName}
                  </span>
                </div>
                <div className="mb-2">
                  <span className="text-muted f-12 d-block">Screen</span>
                  <span className="f-14 text-dark">
                    {selectedEventModal.screenName}
                  </span>
                </div>
                <div className="mb-2">
                  <span className="text-muted f-12 d-block">Clicks Count</span>
                  <span className="f-14 fw-semibold text-primary">
                    {selectedEventModal.count}
                  </span>
                </div>
                {selectedEventModal.userFlow && (
                  <div className="mb-2">
                    <span className="text-muted f-12 d-block">Navigation Flow</span>
                    <span className="f-13 fw-medium text-dark">
                      {selectedEventModal.userFlow}
                    </span>
                  </div>
                )}
                {selectedEventModal.extraData && (
                  <div className="mt-3">
                    <span className="text-muted f-12 d-block mb-1">
                      Context Metadata
                    </span>
                    <pre
                      className="p-2 bg-light border rounded f-12 mb-0"
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        maxHeight: '180px',
                        overflowY: 'auto',
                      }}
                    >
                      {JSON.stringify(selectedEventModal.extraData, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              size="sm"
              onClick={() => setSelectedEventModal(null)}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }

  if (embedded) {
    return <div>{content}</div>;
  }

  return <CardWithTitle title="History">{content}</CardWithTitle>;
};

export default VendorFirebaseData;
